import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { createPortal } from 'react-dom';
import { useMainMenuContext, MainMenuContext } from './MainMenuContext';

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupHeader = styled.button<{ $active: boolean; $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: 0.2s background-color ease-in-out;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight.gray25};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${({ $active }) =>
    $active &&
    css`
      color: ${({ theme }) => theme.colors.primary.main};
      background: ${({ theme }) => theme.colors.highlight.blue25};

      &:hover {
        color: ${({ theme }) => theme.colors.primary.dark};
        background: ${({ theme }) => theme.colors.highlight.blue40};
      }
    `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      padding: 10px;
      justify-content: center;
    `}

  ${({ theme }) => theme.device.mobile} {
    min-height: 44px;
  }
`;

const GroupTitle = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowWrapper = styled.span<{ $open: boolean }>`
  display: flex;
  flex-shrink: 0;
  transition: transform 200ms ease-in-out;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const GroupItems = styled.div<{ $height: number }>`
  overflow: hidden;
  height: ${({ $height }) => $height}px;
  transition: height 200ms ease-in;
`;

const GroupItemsInner = styled.div`
  padding-left: 22px;
`;

const CollapsedPopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.sidebar};
`;

const CollapsedPopup = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  z-index: ${({ theme }) => theme.zIndex.sidebar};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  box-shadow: ${({ theme }) => theme.shadows[2]};
  min-width: 180px;
  padding: 4px 0;
`;

const CollapsedPopupTitle = styled.div`
  padding: 8px 16px 4px;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.disabled};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export interface MenuGroupProps {
  path: string;
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

export function MenuGroup({ path, title, icon, children }: MenuGroupProps) {
  const { openSection, setOpenSection, collapsed } = useMainMenuContext();
  const location = useLocation();
  const groupId = path;
  const isOpen = openSection === groupId;
  const isActive = location.pathname.startsWith(path);

  const innerRef = useRef<HTMLDivElement>(null);
  const [innerHeight, setInnerHeight] = useState(0);
  const headerRef = useRef<HTMLButtonElement>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isActive && !collapsed) {
      setOpenSection(groupId);
    }
  }, [isActive, collapsed, groupId, setOpenSection]);

  useEffect(() => {
    if (isActive && !collapsed && !isOpen) {
      setOpenSection(groupId);
    }
  }, [location.pathname, isActive, collapsed, isOpen, groupId, setOpenSection]);

  useEffect(() => {
    if (innerRef.current) {
      setInnerHeight(isOpen ? innerRef.current.scrollHeight : 0);
    }
  }, [isOpen, children]);

  useEffect(() => {
    if (popupOpen) {
      setPopupOpen(false);
    }
  }, [location.pathname, popupOpen]);

  const handleToggle = () => {
    if (collapsed) {
      if (!popupOpen && headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.top, left: rect.right + 4 });
      }
      setPopupOpen((prev) => !prev);
      return;
    }
    setOpenSection(isOpen ? null : groupId);
  };

  const contextWithoutCollapsed = {
    openSection,
    setOpenSection,
    collapsed: false,
  };

  return (
    <GroupContainer>
      <GroupHeader
        ref={headerRef}
        type="button"
        $active={isActive}
        $collapsed={collapsed}
        onClick={handleToggle}
        title={collapsed ? title : undefined}
      >
        <span style={{ display: 'flex', flexShrink: 0 }}>{icon}</span>
        {!collapsed && (
          <>
            <GroupTitle>{title}</GroupTitle>
            <ArrowWrapper $open={isOpen}>
              <ChevronDownIcon />
            </ArrowWrapper>
          </>
        )}
      </GroupHeader>

      {!collapsed && (
        <GroupItems $height={innerHeight}>
          <GroupItemsInner ref={innerRef}>
            {children}
          </GroupItemsInner>
        </GroupItems>
      )}

      {collapsed && popupOpen && createPortal(
        <>
          <CollapsedPopupOverlay onClick={() => setPopupOpen(false)} />
          <CollapsedPopup $top={popupPos.top} $left={popupPos.left}>
            <CollapsedPopupTitle>{title}</CollapsedPopupTitle>
            <MainMenuContext.Provider value={contextWithoutCollapsed}>
              {children}
            </MainMenuContext.Provider>
          </CollapsedPopup>
        </>,
        document.body,
      )}
    </GroupContainer>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
    </svg>
  );
}
